import { PLOCK_PARAMS, STEP_COMPONENTS, XyProject, STEP_TICKS } from "./xy-project.js";

const VIEWS = [
  ["project", "Project"],
  ["arrange", "Arrange"],
  ["pattern", "Pattern"],
  ["sound", "Sound"],
  ["automation", "Automation"],
  ["audition", "Audition"],
  ["diagnostics", "Diagnostics"],
];

const FILTERS = [
  ["all", "All"],
  ["notes", "Notes"],
  ["plocks", "P-locks"],
  ["components", "Step components"],
  ["mutes", "Scene mutes"],
  ["reused", "Reused patterns"],
];

const state = {
  project: null,
  activeView: "project",
  activeSong: 0,
  activeScene: 0,
  activeTrack: 0,
  activePattern: 0,
  activeFilter: "all",
  dirty: false,
  cleanImage: null,
  undoStack: [],
  redoStack: [],
  selectedStep: null,
  selectedNote: null,
  stepClipboard: null,
  patternMenuOpen: false,
};

const els = {};

function $(selector) {
  return document.querySelector(selector);
}

function clampNumber(value, min, max, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function noteName(midi) {
  const names = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
  return `${names[midi % 12]}${Math.floor(midi / 12) - 1}`;
}

function formatNotes(notes) {
  if (!notes.length) return "·";
  if (notes.length <= 3) return notes.map((note) => `<b>${noteName(note.note)}</b>`).join("");
  const unique = new Set(notes.map((note) => note.note)).size;
  return `<b>${notes.length} notes</b><small>${unique} pitches</small>`;
}

function countByStep(pattern) {
  const map = new Map();
  for (let step = 1; step <= pattern.steps; step += 1) {
    map.set(step, { notes: [], components: [], plocks: [] });
  }
  for (const note of pattern.notes) {
    if (!map.has(note.step)) map.set(note.step, { notes: [], components: [], plocks: [] });
    map.get(note.step).notes.push(note);
  }
  for (const row of pattern.stepComponents) {
    if (map.has(row.step)) map.get(row.step).components = row.components;
  }
  for (const row of pattern.plocks) {
    if (map.has(row.step)) map.get(row.step).plocks = row.values;
  }
  return map;
}

function setStatus(message, tone = "neutral") {
  els.status.textContent = message;
  els.status.dataset.tone = tone;
}

function imagesEqual(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function updateDirtyFlag() {
  state.dirty = Boolean(state.project && !imagesEqual(state.project.image, state.cleanImage));
}

function markDirty(message = "Changes are in memory. Download the edited .xy when ready.") {
  updateDirtyFlag();
  setStatus(message, "ok");
}

function captureHistorySnapshot() {
  if (!state.project) return null;
  return {
    header: new Uint8Array(state.project.header),
    image: new Uint8Array(state.project.image),
    name: state.project.name,
    activeView: state.activeView,
    activeSong: state.activeSong,
    activeScene: state.activeScene,
    activeTrack: state.activeTrack,
    activePattern: state.activePattern,
    activeFilter: state.activeFilter,
    selectedStep: state.selectedStep,
    selectedNote: state.selectedNote ? { ...state.selectedNote } : null,
  };
}

function restoreHistorySnapshot(snapshot) {
  state.project = new XyProject(new Uint8Array(snapshot.header), new Uint8Array(snapshot.image), snapshot.name);
  state.activeView = snapshot.activeView;
  state.activeSong = snapshot.activeSong;
  state.activeScene = Math.min(snapshot.activeScene, state.project.scenes.length - 1);
  state.activeTrack = Math.min(snapshot.activeTrack, state.project.tracks.length - 1);
  const track = state.project.tracks[state.activeTrack];
  state.activePattern = Math.min(snapshot.activePattern, track.patternCount - 1);
  state.activeFilter = snapshot.activeFilter;
  state.selectedStep = snapshot.selectedStep;
  state.selectedNote = snapshot.selectedNote;
  updateDirtyFlag();
}

function commitProjectEdit(message, mutate) {
  const before = captureHistorySnapshot();
  try {
    const result = mutate();
    if (before) {
      state.undoStack.push(before);
      if (state.undoStack.length > 50) state.undoStack.shift();
      state.redoStack = [];
    }
    markDirty(message);
    render();
    return result;
  } catch (error) {
    setStatus(error.message, "bad");
    return null;
  }
}

function undoEdit() {
  if (!state.undoStack.length) return;
  const current = captureHistorySnapshot();
  const previous = state.undoStack.pop();
  if (current) state.redoStack.push(current);
  restoreHistorySnapshot(previous);
  setStatus("Undid last edit.", state.dirty ? "ok" : "neutral");
  render();
}

function redoEdit() {
  if (!state.redoStack.length) return;
  const current = captureHistorySnapshot();
  const next = state.redoStack.pop();
  if (current) state.undoStack.push(current);
  restoreHistorySnapshot(next);
  setStatus("Redid edit.", "ok");
  render();
}

async function loadFile(file) {
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    state.project = XyProject.fromBytes(bytes, file.name);
    state.activeView = "pattern";
    state.activeSong = 0;
    state.activeScene = 0;
    state.activeTrack = 0;
    state.activePattern = 0;
    state.cleanImage = new Uint8Array(state.project.image);
    state.undoStack = [];
    state.redoStack = [];
    state.dirty = false;
    state.selectedStep = null;
    state.selectedNote = null;
    state.stepClipboard = null;
    state.patternMenuOpen = false;
    setStatus(`Loaded ${file.name}. Decoded image: ${state.project.image.length.toLocaleString()} bytes.`, "ok");
    render();
  } catch (error) {
    console.error(error);
    setStatus(error.message, "bad");
  }
}

function setView(view) {
  state.patternMenuOpen = false;
  state.activeView = view;
  state.selectedStep = null;
  state.selectedNote = null;
  render();
}

function setTrackPattern(trackIndex, patternIndex, view = state.activeView) {
  const track = state.project.tracks[trackIndex];
  state.patternMenuOpen = false;
  state.activeTrack = trackIndex;
  state.activePattern = Math.max(0, Math.min(patternIndex, track.patternCount - 1));
  state.activeView = view;
  state.selectedStep = null;
  state.selectedNote = null;
  render();
}

function activePattern() {
  return state.project.getPattern(state.activeTrack, state.activePattern);
}

function selectStep(step) {
  if (!state.project) return;
  const pattern = activePattern();
  const nextStep = clampNumber(step, 1, pattern.steps, 1);
  const notes = pattern.notes
    .filter((note) => note.step === nextStep)
    .sort((a, b) => a.note - b.note || a.tickOffset - b.tickOffset);
  state.selectedStep = nextStep;
  state.selectedNote = notes[0] ? { ...notes[0] } : null;
}

function normalizeClipboardNote(note) {
  return {
    tickOffset: note.tickOffset ?? 0,
    gate: note.gate ?? 240,
    note: note.note,
    velocity: note.velocity ?? 100,
    flags: [...(note.flags ?? [0, 0])],
  };
}

function noteForStep(note, step) {
  const tickOffset = note.tickOffset ?? 0;
  return {
    step,
    tick: (step - 1) * STEP_TICKS + tickOffset,
    tickOffset,
    gate: note.gate ?? 240,
    note: note.note,
    velocity: note.velocity ?? 100,
    flags: [...(note.flags ?? [0, 0])],
  };
}

function render() {
  const hasProject = Boolean(state.project);
  els.empty.hidden = hasProject;
  els.workspace.hidden = !hasProject;
  els.download.disabled = !hasProject;
  els.undo.disabled = !hasProject || !state.undoStack.length;
  els.redo.disabled = !hasProject || !state.redoStack.length;
  els.download.textContent = state.dirty ? "Download edited .xy" : "Download copy .xy";
  renderTabs();
  if (!hasProject) return;

  renderLibrary();
  renderMain();
  renderSidePanel();
}

function renderTabs() {
  els.tabs.replaceChildren();
  for (const [view, label] of VIEWS) {
    const tab = document.createElement("button");
    tab.type = "button";
    tab.className = `tab ${state.activeView === view || (view === "pattern" && state.patternMenuOpen) ? "active" : ""}`;
    tab.textContent = label;
    tab.disabled = !state.project;
    if (view === "pattern") {
      tab.setAttribute("aria-expanded", String(state.patternMenuOpen));
      tab.addEventListener("click", () => {
        state.patternMenuOpen = !state.patternMenuOpen;
        render();
      });
    } else {
      tab.addEventListener("click", () => setView(view));
    }
    els.tabs.append(tab);
  }

  if (state.project && state.patternMenuOpen) {
    const menu = document.createElement("div");
    menu.className = "pattern-nav-menu";
    menu.innerHTML = renderPatternNavMenu(state.project);
    menu.querySelectorAll("[data-pick-track]").forEach((button) => {
      button.addEventListener("click", () => {
        setTrackPattern(Number(button.dataset.pickTrack), Number(button.dataset.pickPattern), "pattern");
      });
    });
    els.tabs.append(menu);
  }
}

function renderPatternNavMenu(project) {
  return `
    <div class="pattern-nav-head">
      <div>
        <h2>Choose Pattern</h2>
      </div>
    </div>
    <div class="pattern-nav-grid">
      ${project.tracks.map((track) => `
        <div class="pattern-nav-row">
          <div class="pattern-nav-track">
            <strong>${track.displayLabel}</strong>
          </div>
          <div class="pattern-nav-patterns">
            ${track.patterns.map((pattern) => {
              const active = track.index === state.activeTrack && pattern.patternIndex === state.activePattern;
              return `
                <button class="${active ? "active" : ""}" type="button" data-pick-track="${track.index}" data-pick-pattern="${pattern.patternIndex}">
                  P${pattern.patternIndex + 1}
                </button>
              `;
            }).join("")}
          </div>
        </div>
      `).join("")}
    </div>
  `;
}

function metric(label, value, hint = "") {
  return `
    <div class="metric">
      <span>${label}</span>
      <strong>${value}</strong>
      ${hint ? `<small>${hint}</small>` : ""}
    </div>
  `;
}

function renderLibrary() {
  const project = state.project;
  const summary = project.getProjectSummary();
  const active = (kind, index) => {
    if (kind === "track") return state.activeTrack === index ? "active" : "";
    if (kind === "scene") return state.activeScene === index ? "active" : "";
    if (kind === "song") return state.activeSong === index ? "active" : "";
    return "";
  };

  els.library.innerHTML = `
    <section class="library-block">
      <h2>Library</h2>
      <button class="library-file active" type="button">${escapeHtml(project.name)}</button>
      <button class="library-file future" type="button" disabled>Open OP-XY folder</button>
      <button class="library-file future" type="button" disabled>Import MIDI</button>
    </section>

    <section class="library-block">
      <h2>Project</h2>
      <div class="mini-stats">
        <span>${summary.patterns} patterns</span>
        <span>${summary.scenes} scenes</span>
        <span>${summary.notes} notes</span>
      </div>
    </section>

    <section class="library-block">
      <h2>Songs</h2>
      <div class="nav-list">
        ${project.songs
          .filter((song) => song.sceneCount || song.index === 0)
          .map((song) => `
            <button class="nav-row ${active("song", song.index)}" type="button" data-song="${song.index}">
              <span>${song.label}</span>
              <small>${song.sceneIds.length ? song.sceneIds.map((id) => id + 1).join(" -> ") : "empty"}</small>
            </button>
          `).join("")}
      </div>
    </section>

    <section class="library-block">
      <h2>Scenes</h2>
      <div class="nav-list compact">
        ${project.scenes.map((scene) => `
          <button class="nav-row ${active("scene", scene.index)}" type="button" data-scene="${scene.index}">
            <span>${scene.label}</span>
            <small>${scene.mutes.filter(Boolean).length || ""}${scene.mutes.filter(Boolean).length ? " mutes" : ""}</small>
          </button>
        `).join("")}
      </div>
    </section>

    <section class="library-block">
      <h2>Tracks</h2>
      <div class="nav-list">
        ${project.tracks.map((track) => {
          const trackSummary = project.summarizeTrack(track);
          return `
            <button class="nav-row ${active("track", track.index)}" type="button" data-track="${track.index}">
              <span>${track.displayLabel}${track.midiChannel ? ` / ch ${track.midiChannel}` : ""}</span>
              <small>${track.patternCount} patterns, ${trackSummary.notes} notes</small>
            </button>
          `;
        }).join("")}
      </div>
    </section>
  `;

  els.library.querySelectorAll("[data-song]").forEach((button) => {
    button.addEventListener("click", () => {
      state.activeSong = Number(button.dataset.song);
      state.activeView = "arrange";
      render();
    });
  });
  els.library.querySelectorAll("[data-scene]").forEach((button) => {
    button.addEventListener("click", () => {
      state.activeScene = Number(button.dataset.scene);
      state.activeView = "arrange";
      render();
    });
  });
  els.library.querySelectorAll("[data-track]").forEach((button) => {
    button.addEventListener("click", () => {
      setTrackPattern(Number(button.dataset.track), 0, "pattern");
    });
  });
}

function renderMain() {
  if (state.activeView === "project") renderProjectView();
  if (state.activeView === "arrange") renderArrangeView();
  if (state.activeView === "pattern") renderPatternView();
  if (state.activeView === "sound") renderSoundView();
  if (state.activeView === "automation") renderAutomationView();
  if (state.activeView === "audition") renderAuditionView();
  if (state.activeView === "diagnostics") renderDiagnosticsView();
}

function breadcrumb(extra = "") {
  const project = state.project;
  if (!project) return "";
  const track = project.tracks[state.activeTrack];
  const pattern = project.getPattern(state.activeTrack, state.activePattern);
  const scene = project.scenes[state.activeScene];
  const crumbs = [
    project.name,
    scene?.label,
    track?.displayLabel || track?.label,
    pattern ? `P${pattern.patternIndex + 1}` : null,
    extra,
  ].filter(Boolean);
  return `<div class="breadcrumb">${crumbs.map((item) => `<span>${escapeHtml(item)}</span>`).join("")}</div>`;
}

function filterBar() {
  return `
    <div class="filter-bar">
      ${FILTERS.map(([value, label]) => `
        <button class="filter-chip ${state.activeFilter === value ? "active" : ""}" type="button" data-filter="${value}">
          ${label}
        </button>
      `).join("")}
    </div>
  `;
}

function bindFilters(root) {
  root.querySelectorAll("[data-filter]").forEach((button) => {
    button.addEventListener("click", () => {
      state.activeFilter = button.dataset.filter;
      render();
    });
  });
}

function summaryMatchesFilter(cellSummary) {
  if (state.activeFilter === "all") return true;
  if (state.activeFilter === "notes") return cellSummary.summary.notes > 0;
  if (state.activeFilter === "plocks") return cellSummary.summary.plockCount > 0;
  if (state.activeFilter === "components") return cellSummary.summary.componentCount > 0;
  if (state.activeFilter === "mutes") return cellSummary.sequencerMuted;
  if (state.activeFilter === "reused") return cellSummary.reused;
  return true;
}

function renderProjectView() {
  const summary = state.project.getProjectSummary();
  const findings = state.project.getFindings();

  els.main.innerHTML = `
    ${breadcrumb()}
    <div class="view-head">
      <div>
        <h2>Project</h2>
        <h1>${escapeHtml(summary.name)}</h1>
      </div>
      <p>Big-screen project viewer for the OP-XY song, scene, pattern, sound, and automation structure.</p>
    </div>

    ${filterBar()}

    <div class="metric-grid">
      ${metric("Tempo", `${summary.tempo.toFixed(1)} BPM`)}
      ${metric("Songs", summary.songs, `${summary.songSlots} slots`)}
      ${metric("Scenes", summary.scenes)}
      ${metric("Patterns", summary.patterns)}
      ${metric("Notes", summary.notes)}
      ${metric("P-locks", summary.plockCount)}
      ${metric("Step components", summary.componentCount)}
      ${metric("Scene mutes", summary.sceneSequencerMutes)}
    </div>

    <section class="surface-section">
      <div class="section-head">
        <h2>Tracks</h2>
        <span>Sequence and sound overview</span>
      </div>
      <table class="data-table">
        <thead>
          <tr>
            <th>Track</th><th>Sound</th><th>Patterns</th><th>Notes</th><th>P-locks</th><th>Step components</th><th>MIDI</th>
          </tr>
        </thead>
        <tbody>
          ${state.project.tracks.map((track) => {
            const summary = state.project.summarizeTrack(track);
            return `
              <tr data-jump-track="${track.index}">
                <td><button class="link-button" type="button">${track.displayLabel}</button><small>${track.group}</small></td>
                <td><span class="mono">engine ${track.engineId}</span><small>${escapeHtml(track.presetPath || "no preset path")}</small></td>
                <td>${track.patternCount}</td>
                <td>${summary.notes}</td>
                <td>${summary.plockCount}</td>
                <td>${summary.componentCount}</td>
                <td>${track.midiChannel ?? "off"}</td>
              </tr>
            `;
          }).join("")}
        </tbody>
      </table>
    </section>

    <section class="surface-section">
      <div class="section-head">
        <h2>Surfaced Behavior</h2>
        <span>Useful context, not the whole interface</span>
      </div>
      <div class="finding-grid">
        ${findings.slice(0, 10).map((finding) => findingCard(finding)).join("") || "<p>No surfaced behavior found in decoded fields.</p>"}
      </div>
    </section>
  `;

  els.main.querySelectorAll("[data-jump-track]").forEach((row) => {
    row.addEventListener("click", () => setTrackPattern(Number(row.dataset.jumpTrack), 0, "pattern"));
  });
  bindFilters(els.main);
  bindFindingClicks(els.main);
}

function findingCard(finding) {
  return `
    <button class="finding ${finding.severity}" type="button"
      ${Number.isInteger(finding.trackIndex) ? `data-finding-track="${finding.trackIndex}"` : ""}
      ${Number.isInteger(finding.patternIndex) ? `data-finding-pattern="${finding.patternIndex}"` : ""}
      ${Number.isInteger(finding.sceneIndex) ? `data-finding-scene="${finding.sceneIndex}"` : ""}>
      <strong>${escapeHtml(finding.title)}</strong>
      <span>${escapeHtml(finding.detail)}</span>
    </button>
  `;
}

function bindFindingClicks(root) {
  root.querySelectorAll(".finding").forEach((button) => {
    button.addEventListener("click", () => {
      if (button.dataset.findingScene) {
        state.activeScene = Number(button.dataset.findingScene);
        state.activeView = "arrange";
      }
      if (button.dataset.findingTrack) {
        setTrackPattern(
          Number(button.dataset.findingTrack),
          Number(button.dataset.findingPattern || 0),
          "pattern",
        );
        return;
      }
      render();
    });
  });
}

function renderArrangeView() {
  const project = state.project;
  els.main.innerHTML = `
    ${breadcrumb()}
    <div class="view-head">
      <div>
        <h2>Arrange</h2>
        <h1>Scenes x Tracks</h1>
      </div>
      <p>Pattern assignments are the primary view. Mutes, p-locks, and step components are overlays so hidden state is visible without taking over.</p>
    </div>
    ${filterBar()}
    <div class="matrix-wrap">
      <table class="arrange-matrix">
        <thead>
          <tr>
            <th>Scene</th>
            ${project.tracks.map((track) => `<th>${track.displayLabel}<small>${track.patternCount} patterns</small></th>`).join("")}
          </tr>
        </thead>
        <tbody>
          ${project.scenes.map((scene) => `
            <tr class="${state.activeScene === scene.index ? "active-row" : ""}">
              <th>${scene.label}</th>
              ${project.tracks.map((track) => sceneCell(scene, track)).join("")}
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
    <section class="surface-section">
      <div class="section-head">
        <h2>Song Chain</h2>
        <span>${project.songs[state.activeSong]?.label || "Song 1"}</span>
      </div>
      <div class="song-chain">
        ${(project.songs[state.activeSong]?.sceneIds || []).map((id, index) => `
          <button type="button" class="scene-chip" data-chain-scene="${id + 1}">
            <span>${index + 1}</span> Scene ${id + 1}
          </button>
        `).join("") || "<p>No explicit song chain decoded.</p>"}
      </div>
    </section>
  `;

  els.main.querySelectorAll("[data-cell-track]").forEach((cell) => {
    cell.addEventListener("click", () => {
      state.activeScene = Number(cell.dataset.cellScene);
      state.activeTrack = Number(cell.dataset.cellTrack);
      state.activePattern = Number(cell.dataset.cellPattern);
      state.selectedStep = null;
      state.selectedNote = null;
      render();
    });
  });
  els.main.querySelectorAll("[data-chain-scene]").forEach((button) => {
    button.addEventListener("click", () => {
      state.activeScene = Number(button.dataset.chainScene);
      render();
    });
  });
  bindFilters(els.main);
}

function sceneCell(scene, track) {
  const cell = state.project.getSceneCellSummary(scene.index, track.index);
  const { patternIndex, pattern, summary, sequencerMuted: muted, reused } = cell;
  const matched = summaryMatchesFilter(cell);
  const selectedUsage = track.index === state.activeTrack && patternIndex === state.activePattern;
  const selectedCell = scene.index === state.activeScene && track.index === state.activeTrack;
  const classes = [
    muted ? "is-muted" : "",
    summary.plockCount ? "has-plocks" : "",
    summary.componentCount ? "has-components" : "",
    summary.notes ? "has-notes" : "",
    reused ? "is-reused" : "",
    selectedUsage ? "selected-usage" : "",
    selectedCell ? "selected-cell" : "",
    matched ? "" : "filtered-out",
  ].filter(Boolean).join(" ");

  return `
    <td>
      <button class="matrix-cell ${classes}" type="button"
        data-cell-scene="${scene.index}"
        data-cell-track="${track.index}"
        data-cell-pattern="${patternIndex}">
        <strong>P${patternIndex + 1}</strong>
        <span>${summary.notes} notes / ${pattern.steps} steps</span>
        <em>
          ${muted ? "<b>MUTE-S</b>" : ""}
          ${reused ? `<b>USED ${cell.usage.length}</b>` : ""}
          ${summary.plockCount ? `<b>PL ${summary.plockCount}</b>` : ""}
          ${summary.componentCount ? `<b>SC ${summary.componentCount}</b>` : ""}
        </em>
      </button>
    </td>
  `;
}

function renderPatternView() {
  const project = state.project;
  const track = project.tracks[state.activeTrack];
  const pattern = activePattern();
  const summary = project.summarizePattern(pattern);
  const usage = project.getPatternUsage(state.activeTrack, state.activePattern);
  const byStep = countByStep(pattern);

  els.main.innerHTML = `
    ${breadcrumb()}
    <div class="view-head">
      <div>
        <h2>Pattern</h2>
        <h1>${track.displayLabel} / P${pattern.patternIndex + 1}</h1>
      </div>
      <p>${usage.length ? `Used by ${usage.map((item) => item.label).join(", ")}` : "Not referenced by decoded scenes."}</p>
    </div>

    <div class="pattern-toolbar">
      <div class="segments compact">${track.patterns.map((pat) => `
        <button class="seg ${pat.patternIndex === pattern.patternIndex ? "active" : ""}" type="button" data-pattern="${pat.patternIndex}">
          P${pat.patternIndex + 1} <small>${pat.noteCount} notes</small>
        </button>
      `).join("")}</div>
      <div class="pattern-stats">
        <span><b>${summary.notes}</b> notes</span>
        <span><b>${summary.plockCount}</b> p-locks</span>
        <span><b>${summary.componentCount}</b> components</span>
        <span><b>${summary.nudgedNotes}</b> nudges</span>
      </div>
    </div>

    ${renderPatternClipEditor(pattern, byStep)}
  `;

  els.main.querySelectorAll("[data-pattern]").forEach((button) => {
    button.addEventListener("click", () => setTrackPattern(state.activeTrack, Number(button.dataset.pattern), "pattern"));
  });
  els.main.querySelectorAll("[data-step]").forEach((button) => {
    button.addEventListener("click", () => {
      selectStep(Number(button.dataset.step));
      render();
    });
  });
  els.main.querySelectorAll("[data-note-block]").forEach((button) => {
    bindNoteBlockDrag(button);
  });
}

function selectNoteBlock(noteIndex) {
  const note = activePattern().notes.find((item) => item.index === noteIndex);
  if (!note) return;
  state.selectedStep = note.step;
  state.selectedNote = { ...note };
  render();
}

function bindNoteBlockDrag(button) {
  button.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) return;
    const noteIndex = Number(button.dataset.noteBlock);
    const note = activePattern().notes.find((item) => item.index === noteIndex);
    const grid = button.closest(".roll-grid");
    if (!note || !grid) return;

    event.preventDefault();
    event.stopPropagation();

    const pattern = activePattern();
    const pitches = grid.dataset.pitches.split(",").map(Number);
    const mode = event.target.closest(".note-resize-handle") ? "resize" : "move";
    const initialStep = note.step;
    const initialPitch = note.note;
    const initialGate = note.gate;
    const initialTickOffset = note.tickOffset || 0;
    const startTick = (initialStep - 1) * STEP_TICKS + initialTickOffset;
    const maxGate = pattern.steps * STEP_TICKS - startTick;
    const gateSnap = STEP_TICKS / 8;
    const minGate = Math.min(gateSnap, maxGate);
    let targetStep = initialStep;
    let targetPitch = initialPitch;
    let targetGate = initialGate;
    let moved = false;
    let insideGrid = true;

    const isInsideGrid = (clientX, clientY) => {
      const rect = grid.getBoundingClientRect();
      return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
    };

    const previewAt = (clientX, clientY) => {
      const rect = grid.getBoundingClientRect();
      insideGrid = isInsideGrid(clientX, clientY);
      button.classList.toggle("invalid-drop", !insideGrid);
      if (!insideGrid) return;

      const stepWidth = rect.width / pattern.steps;
      const rowHeight = rect.height / pitches.length;

      if (mode === "resize") {
        const rawGate = ((clientX - rect.left) / rect.width) * pattern.steps * STEP_TICKS - startTick;
        targetGate = clampNumber(Math.round(rawGate / gateSnap) * gateSnap, minGate, maxGate, initialGate);
        moved = moved || targetGate !== initialGate;
        button.classList.add("resizing");
        button.style.setProperty("--width", `${Math.max(1.8, (targetGate / (pattern.steps * STEP_TICKS)) * 100).toFixed(3)}%`);
        button.title = `${noteName(initialPitch)} step ${initialStep}, velocity ${note.velocity}, gate ${targetGate}${initialTickOffset ? `, nudge +${initialTickOffset}` : ""}`;
      } else {
        const nextStep = clampNumber(Math.floor((clientX - rect.left) / stepWidth) + 1, 1, pattern.steps, initialStep);
        const rowIndex = clampNumber(Math.floor((clientY - rect.top) / rowHeight), 0, pitches.length - 1, pitches.indexOf(initialPitch));
        const nextPitch = pitches[rowIndex];

        targetStep = nextStep;
        targetPitch = nextPitch;
        moved = moved || targetStep !== initialStep || targetPitch !== initialPitch;
        button.classList.add("dragging");
        button.style.setProperty("--left", `${(((targetStep - 1) * STEP_TICKS) / (pattern.steps * STEP_TICKS) * 100).toFixed(3)}%`);
        button.style.setProperty("--lane", rowIndex + 1);
        button.querySelector(".note-label").textContent = noteName(targetPitch);
        grid.dataset.dragStep = String(targetStep);
      }
    };

    const onMove = (moveEvent) => {
      previewAt(moveEvent.clientX, moveEvent.clientY);
    };

    const onUp = (upEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      button.classList.remove("dragging");
      button.classList.remove("resizing");
      button.classList.remove("invalid-drop");
      delete grid.dataset.dragStep;

      if (!insideGrid) {
        render();
        return;
      }

      if (!moved) {
        selectNoteBlock(noteIndex);
        return;
      }

      if (mode === "resize") {
        commitProjectEdit(`Resized ${noteName(initialPitch)} on step ${initialStep} to ${targetGate} ticks.`, () => {
          state.project.updateNote(state.activeTrack, state.activePattern, noteIndex, {
            gate: targetGate,
          });
          state.selectedStep = initialStep;
          state.selectedNote = null;
        });
      } else {
        commitProjectEdit(`Moved ${noteName(initialPitch)} to ${noteName(targetPitch)} on step ${targetStep}.`, () => {
          state.project.updateNote(state.activeTrack, state.activePattern, noteIndex, {
            step: targetStep,
            note: targetPitch,
            tickOffset: 0,
          });
          state.selectedStep = targetStep;
          state.selectedNote = null;
        });
      }
    };

    const onCancel = (cancelEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      button.classList.remove("dragging");
      button.classList.remove("resizing");
      button.classList.remove("invalid-drop");
      delete grid.dataset.dragStep;
      render();
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
  });
}

function renderPatternClipEditor(pattern, byStep) {
  return `
    <section class="clip-editor">
      <div class="section-head clip-head">
        <div>
          <h2>Clip Editor</h2>
          <span>Notes read as a piano roll; OP-XY-only behavior stays visible in lanes below.</span>
        </div>
        <div class="clip-legend">
          <span><i class="legend-note"></i>note</span>
          <span><i class="legend-plock"></i>p-lock</span>
          <span><i class="legend-component"></i>component</span>
        </div>
      </div>
      <div class="clip-scroll">
        ${renderTimelineRuler(pattern)}
        ${renderNoteRoll(pattern)}
        ${renderBehaviorStrip(pattern, byStep)}
      </div>
    </section>
  `;
}

function renderTimelineRuler(pattern) {
  const steps = Array.from({ length: pattern.steps }, (_, index) => index + 1);

  return `
    <div class="timeline-ruler" style="--steps:${pattern.steps}">
      <div class="ruler-label">Step</div>
      <div class="ruler-grid">
        ${steps.map((step) => `
          <button class="${step % 4 === 1 ? "barline" : ""} ${state.selectedStep === step ? "selected-step" : ""}" type="button" data-step="${step}">
            ${step}
          </button>
        `).join("")}
      </div>
    </div>
  `;
}

function renderNoteRoll(pattern) {
  const pitchRows = getPitchRows(pattern);
  const rollNotes = pattern.notes.map((note) => {
    const left = (((note.step - 1) * STEP_TICKS + (note.tickOffset || 0)) / (pattern.steps * STEP_TICKS)) * 100;
    const width = Math.max(1.8, (note.gate / (pattern.steps * STEP_TICKS)) * 100);
    const flags = [
      note.tickOffset ? "nudged" : "",
      note.gate > STEP_TICKS ? "long-gate" : "",
      note.velocity === 0 ? "zero-velocity" : "",
      state.selectedNote?.index === note.index ? "selected-note" : "",
    ].filter(Boolean).join(" ");

    return `
      <button class="note-block ${flags}"
        type="button"
        data-note-block="${note.index}"
        draggable="false"
        style="--left:${left.toFixed(3)}%;--width:${width.toFixed(3)}%;--lane:${pitchRows.indexOf(note.note) + 1};--velocity:${Math.max(0.2, note.velocity / 127).toFixed(3)}"
        title="${noteName(note.note)} step ${note.step}, velocity ${note.velocity}, gate ${note.gate}${note.tickOffset ? `, nudge +${note.tickOffset}` : ""}">
        <span class="note-label">${noteName(note.note)}</span>
        <i class="note-resize-handle" aria-hidden="true"></i>
      </button>
    `;
  }).join("");

  return `
    <div class="note-roll" style="--steps:${pattern.steps};--rows:${pitchRows.length}">
      <div class="pitch-axis">
        ${pitchRows.map((pitch) => `<span>${noteName(pitch)}</span>`).join("")}
      </div>
      <div class="roll-grid" data-pitches="${pitchRows.join(",")}" data-steps="${pattern.steps}">
        ${pitchRows.map((pitch) => `<div class="pitch-row" style="grid-row:${pitchRows.indexOf(pitch) + 1}"></div>`).join("")}
        ${Array.from({ length: pattern.steps }, (_, index) => `<button class="step-hit ${(index + 1) % 4 === 1 ? "barline" : ""} ${state.selectedStep === index + 1 ? "selected-step" : ""}" type="button" data-step="${index + 1}" style="--step:${index + 1}"></button>`).join("")}
        ${rollNotes || `<div class="empty-roll">No notes yet. Select a step, then add notes from the inspector.</div>`}
      </div>
    </div>
  `;
}

function getPitchRows(pattern) {
  if (!pattern.notes.length) {
    return Array.from({ length: 13 }, (_, index) => 72 - index);
  }

  const notes = pattern.notes.map((note) => note.note);
  const low = Math.max(0, Math.min(...notes) - 2);
  const high = Math.min(127, Math.max(...notes) + 2);
  if (high - low <= 24) {
    return Array.from({ length: high - low + 1 }, (_, index) => high - index);
  }

  return [...new Set(notes)].sort((a, b) => b - a);
}

function renderBehaviorStrip(pattern, byStep) {
  const steps = Array.from({ length: pattern.steps }, (_, index) => index + 1);
  const lane = (label, className, renderer) => `
    <div class="behavior-row ${className}" style="--steps:${pattern.steps}">
      <div class="behavior-label">${label}</div>
      <div class="behavior-cells" style="--steps:${pattern.steps}">
        ${steps.map((step) => `
          <button class="${step % 4 === 1 ? "barline" : ""} ${state.selectedStep === step ? "selected-step" : ""}" type="button" data-step="${step}">
            ${renderer(step, byStep.get(step))}
          </button>
        `).join("")}
      </div>
    </div>
  `;

  return `
    <div class="behavior-strip">
      ${lane("Velocity", "velocity-lane", (_step, data) => {
        if (!data.notes.length) return "";
        const velocity = Math.round(data.notes.reduce((sum, note) => sum + note.velocity, 0) / data.notes.length);
        return `<span class="velocity-bar" style="--height:${Math.max(8, velocity / 127 * 100).toFixed(1)}%"></span><em>${velocity}</em>`;
      })}
      ${lane("Gate / nudge", "gate-lane", (_step, data) => {
        if (!data.notes.length) return "";
        const longGate = data.notes.some((note) => note.gate > STEP_TICKS);
        const nudged = data.notes.some((note) => note.tickOffset);
        return `${longGate ? `<span class="gate-chip">long</span>` : ""}${nudged ? `<span class="nudge-chip">+${data.notes.map((note) => note.tickOffset).filter(Boolean).join("/")}</span>` : ""}`;
      })}
      ${lane("P-locks", "plock-lane", (_step, data) => data.plocks.length ? `<span class="lane-pill red">${data.plocks.length}</span>` : "")}
      ${lane("Components", "component-lane", (_step, data) => data.components.length ? `<span class="lane-pill amber">${data.components.length}</span>` : "")}
    </div>
  `;
}

function renderSoundView() {
  els.main.innerHTML = `
    <div class="view-head">
      <div>
        <h2>Sound</h2>
        <h1>Track engines, presets, and future sample library</h1>
      </div>
      <p>First pass reads sound identity from the project. Later this becomes the patch/sample builder and OP-XY folder browser.</p>
    </div>
    <table class="data-table">
      <thead>
        <tr><th>Track</th><th>Engine</th><th>Preset path</th><th>Future affordance</th></tr>
      </thead>
      <tbody>
        ${state.project.tracks.map((track) => `
          <tr>
            <td>${track.displayLabel}<small>${track.group}</small></td>
            <td><span class="mono">${track.engineId}</span></td>
            <td><span class="mono">${escapeHtml(track.presetPath || "not decoded")}</span></td>
            <td>${track.index === 0 ? "drum voice/sample table" : "engine params / patch builder"}</td>
          </tr>
        `).join("")}
      </tbody>
    </table>
    <div class="future-panel">
      <strong>Planned:</strong> open copied OP-XY storage, resolve WAV/AIF samples, preview sample-backed patches in Web Audio, and copy presets between projects.
    </div>
  `;
}

function renderAutomationView() {
  const pattern = activePattern();
  const rows = pattern.plocks.filter((row) => row.values.length);
  els.main.innerHTML = `
    <div class="view-head">
      <div>
        <h2>Automation</h2>
        <h1>T${state.activeTrack + 1} / P${state.activePattern + 1}</h1>
      </div>
      <p>Step p-locks are shown as lanes. High-resolution pitch/mod/aftertouch lanes are reserved for a later decoder pass.</p>
    </div>
    <div class="automation-list">
      ${rows.map((row) => `
        <div class="automation-row">
          <strong>Step ${row.step}</strong>
          <span>${row.values.map((value) => `${value.name}: ${value.value}`).join(", ")}</span>
        </div>
      `).join("") || "<p>No visible parameter locks in this pattern.</p>"}
    </div>
  `;
}

function renderAuditionView() {
  els.main.innerHTML = `
    <div class="view-head">
      <div>
        <h2>Audition</h2>
        <h1>Playback lab</h1>
      </div>
      <p>This is the future bridge from file editing to hearing edits through the real OP-XY or local sample playback.</p>
    </div>
    <div class="transport-panel">
      <button type="button" disabled>Connect OP-XY MIDI</button>
      <button type="button" disabled>Play pattern</button>
      <button type="button" disabled>Play scene</button>
      <button type="button" disabled>Panic</button>
    </div>
    <div class="future-grid">
      ${metric("Hardware audition", "planned", "Send decoded notes/CCs to the OP-XY")}
      ${metric("Sample audition", "planned", "Use WAV/AIF files from copied storage")}
      ${metric("Hybrid playback", "planned", "Hardware synths plus local sample tracks")}
      ${metric("Scene selection", "research", "Map OP-XY remote commands")}
    </div>
  `;
}

function renderDiagnosticsView() {
  const findings = state.project.getFindings();
  els.main.innerHTML = `
    <div class="view-head">
      <div>
        <h2>Diagnostics</h2>
        <h1>Validation and surfaced behavior</h1>
      </div>
      <p>Diagnostics support the editor. They are available when needed, but the primary workspace remains project viewing and editing.</p>
    </div>
    <div class="finding-list">
      ${findings.map((finding) => findingCard(finding)).join("") || "<p>No findings.</p>"}
    </div>
  `;
  bindFindingClicks(els.main);
}

function renderSidePanel() {
  if (!state.project) return;
  if (state.activeView === "arrange") {
    renderArrangeSidePanel();
    return;
  }
  if (state.activeView === "pattern") {
    renderPatternSidePanel();
    return;
  }

  const findings = state.project.getFindings().slice(0, 8);
  els.side.innerHTML = `
    <section>
      <h2>Workbench</h2>
      <p>Open one project today. Later this sidebar becomes the live project library, sample resolver, and import queue.</p>
    </section>
    <section>
      <h2>Surfaced Behavior</h2>
      <div class="finding-list compact">
        ${findings.map((finding) => findingCard(finding)).join("") || "<p>No findings.</p>"}
      </div>
    </section>
  `;
  bindFindingClicks(els.side);
}

function renderArrangeSidePanel() {
  const project = state.project;
  const cell = project.getSceneCellSummary(state.activeScene, state.activeTrack);
  if (!cell) {
    els.side.innerHTML = "<section><h2>Arrange Editor</h2><p>Select a scene cell.</p></section>";
    return;
  }

  const options = project.getTrackPatternOptions(state.activeTrack);
  const muteLabel = cell.track.group === "aux" ? "Mute sequencer lane" : "Sequencer mute";

  els.side.innerHTML = `
    <section>
      <h2>Arrange Editor</h2>
      <div class="context-card">
        <strong>${cell.scene.label} / ${cell.track.displayLabel}</strong>
        <span>${cell.summary.notes} notes, ${cell.summary.plockCount} p-locks, ${cell.summary.componentCount} step components</span>
      </div>
      <form id="arrangeForm" class="stack-form">
        <label>Pattern
          <select id="scenePattern">
            ${options.map((option) => `
              <option value="${option.pattern.patternIndex}" ${option.pattern.patternIndex === cell.patternIndex ? "selected" : ""}>
                P${option.pattern.patternIndex + 1} - ${option.summary.notes} notes, ${option.usage.length} scenes
              </option>
            `).join("")}
          </select>
        </label>
        <label class="check-row">
          <input id="sceneMute" type="checkbox" ${cell.sequencerMuted ? "checked" : ""} />
          <span>${muteLabel}</span>
        </label>
      </form>
      <div class="form-actions">
        <button id="openPattern" class="primary" type="button">Open pattern</button>
        <button id="duplicateForScene" class="ghost" type="button" ${cell.track.patternCount >= 9 ? "disabled" : ""}>Duplicate for scene</button>
        <button id="openAutomation" class="ghost" type="button">Automation</button>
      </div>
    </section>
    <section>
      <h2>Pattern Usage</h2>
      <ul>
        ${cell.usage.map((usage) => `<li>${usage.label}${usage.muted ? " (muted)" : ""}</li>`).join("") || "<li>Not used by decoded scenes.</li>"}
      </ul>
    </section>
    <section>
      <h2>Visible Layers</h2>
      <ul>
        <li>${cell.summary.notes} notes across ${cell.pattern.steps} steps</li>
        <li>${cell.summary.componentCount} step components on ${cell.summary.componentSteps} steps</li>
        <li>${cell.summary.plockCount} parameter locks on ${cell.summary.plockSteps} steps</li>
      </ul>
    </section>
  `;

  $("#scenePattern").addEventListener("change", (event) => {
    const patternIndex = Number(event.target.value);
    commitProjectEdit(`${cell.scene.label} ${cell.track.displayLabel} now uses P${patternIndex + 1}.`, () => {
      project.setScenePattern(state.activeScene, state.activeTrack, patternIndex);
      state.activePattern = patternIndex;
      state.selectedStep = null;
      state.selectedNote = null;
    });
  });

  $("#sceneMute").addEventListener("change", (event) => {
    commitProjectEdit(`${cell.scene.label} ${cell.track.displayLabel} sequencer mute ${event.target.checked ? "enabled" : "disabled"}.`, () => {
      project.setSceneMute(state.activeScene, state.activeTrack, event.target.checked);
    });
  });

  $("#openPattern").addEventListener("click", () => {
    setTrackPattern(state.activeTrack, state.activePattern, "pattern");
  });

  $("#duplicateForScene").addEventListener("click", () => {
    duplicateActivePatternForScene(cell);
  });

  $("#openAutomation").addEventListener("click", () => {
    state.activeView = "automation";
    render();
  });
}

function duplicateActivePatternForScene(cell) {
  commitProjectEdit(`${cell.scene.label} ${cell.track.displayLabel} pattern duplicated for this scene.`, () => {
    const newPatternIndex = state.project.duplicatePatternForScene(
      state.activeScene,
      state.activeTrack,
      state.activePattern,
    );
    state.activePattern = newPatternIndex;
    state.selectedStep = null;
    state.selectedNote = null;
    return newPatternIndex;
  });
}

function renderPatternSidePanel() {
  const pattern = activePattern();
  const selectedStep = state.selectedStep;
  const stepNotes = selectedStep
    ? pattern.notes.filter((note) => note.step === selectedStep).sort((a, b) => a.note - b.note || a.tickOffset - b.tickOffset)
    : [];
  const selectedStepPlocks = selectedStep
    ? pattern.plocks.find((row) => row.step === selectedStep)?.values ?? []
    : [];
  const selectedStepComponents = selectedStep
    ? pattern.stepComponents.find((row) => row.step === selectedStep)?.components ?? []
    : [];
  const selected = state.selectedNote;
  const canPaste = Boolean(selectedStep && state.stepClipboard?.notes?.length);
  const canDuplicateNext = Boolean(selectedStep && stepNotes.length && selectedStep < pattern.steps);
  const componentRows = pattern.stepComponents
    .filter((row) => row.components.length)
    .map((row) => `<li>Step ${row.step}: ${row.components.map((c) => `${c.name}=${c.value}`).join(", ")}</li>`)
    .join("");
  const plockRows = pattern.plocks
    .filter((row) => row.values.length)
    .map((row) => `<li>Step ${row.step}: ${row.values.map((p) => `${p.name}=${p.value}`).join(", ")}</li>`)
    .join("");

  els.side.innerHTML = `
    <section class="inspector-section">
      <div class="inspector-title">
        <div>
          <h2>Clip Inspector</h2>
          <strong id="noSelection">${selectedStep ? `Step ${selectedStep}` : "No step selected"}</strong>
        </div>
        <span>${selectedStep ? `${stepNotes.length} note${stepNotes.length === 1 ? "" : "s"}` : "Click the timeline"}</span>
      </div>
      <div class="event-list" ${selectedStep ? "" : "hidden"}>
        ${stepNotes.map((note) => `
          <button class="${selected?.index === note.index ? "active" : ""}" type="button" data-select-note="${note.index}">
            <strong>${noteName(note.note)}</strong>
            <span>vel ${note.velocity}</span>
            <span>gate ${note.gate}</span>
            ${note.tickOffset ? `<span>+${note.tickOffset}</span>` : ""}
          </button>
        `).join("") || "<span class=\"empty-event\">No notes on this step yet.</span>"}
      </div>
      <button id="addStepNote" class="ghost full-width" type="button" ${selectedStep ? "" : "disabled"}>Add note</button>
      <div class="step-actions" ${selectedStep ? "" : "hidden"}>
        <button class="ghost" type="button" data-step-action="copy" ${stepNotes.length ? "" : "disabled"}>Copy notes</button>
        <button class="ghost" type="button" data-step-action="paste" ${canPaste ? "" : "disabled"}>Paste notes</button>
        <button class="ghost" type="button" data-step-action="duplicate-next" ${canDuplicateNext ? "" : "disabled"}>Duplicate next</button>
        <button class="danger" type="button" data-step-action="clear" ${stepNotes.length ? "" : "disabled"}>Clear notes</button>
      </div>
      <p class="shortcut-hint" ${selectedStep ? "" : "hidden"}>
        Left/right selects steps. Cmd/Ctrl+C copies notes, Cmd/Ctrl+V pastes, Cmd/Ctrl+D duplicates next, Delete clears notes.
      </p>
      <form id="noteForm" class="note-form inspector-form" ${selected ? "" : "hidden"}>
        <input id="noteIndex" type="hidden" value="${selected?.index ?? ""}" />
        <label>Step<input id="noteStep" type="number" min="1" max="${pattern.steps}" value="${selected?.step ?? selectedStep ?? ""}" /></label>
        <label>Pitch<input id="notePitch" type="number" min="0" max="127" value="${selected?.note ?? 60}" /></label>
        <label>Velocity<input id="noteVelocity" type="number" min="0" max="127" value="${selected?.velocity ?? 100}" /></label>
        <label>Gate ticks<input id="noteGate" type="number" min="1" max="7680" value="${selected?.gate ?? 240}" /></label>
        <label>Tick offset<input id="noteTickOffset" type="number" min="0" max="479" value="${selected?.tickOffset ?? 0}" /></label>
        <div class="form-actions">
          <button class="primary" type="submit">Save note</button>
          <button id="deleteNote" class="danger" type="button" ${selected?.index === null ? "disabled" : ""}>Delete</button>
        </div>
      </form>
    </section>
    <section class="inspector-section">
      <div class="inspector-title">
        <div>
          <h2>Automation</h2>
          <strong>${selectedStep ? "P-locks" : "No step selected"}</strong>
        </div>
        <span>${selectedStepPlocks.length}</span>
      </div>
      <div class="plock-list" ${selectedStep ? "" : "hidden"}>
        ${selectedStepPlocks.map((lock) => `
          <div class="plock-row">
            <span>${escapeHtml(lock.name)}</span>
            <strong>${lock.value}</strong>
            <button class="danger slim" type="button" data-clear-plock="${lock.byteOffset}">Clear</button>
          </div>
        `).join("") || "<p>No p-locks on this step.</p>"}
      </div>
      <form id="plockForm" class="stack-form" ${selectedStep ? "" : "hidden"}>
        <label>Parameter
          <select id="plockParam">
            ${PLOCK_PARAMS.map(([name, byteOffset]) => `<option value="${byteOffset}">${escapeHtml(name)}</option>`).join("")}
          </select>
        </label>
        <label>Value<input id="plockValue" type="number" min="0" max="65535" value="100" /></label>
        <div class="form-actions">
          <button class="primary" type="submit">Set p-lock</button>
        </div>
      </form>
    </section>
    <section class="inspector-section">
      <div class="inspector-title">
        <div>
          <h2>Step Behavior</h2>
          <strong>${selectedStep ? "Components" : "No step selected"}</strong>
        </div>
        <span>${selectedStepComponents.length}</span>
      </div>
      <div class="component-list" ${selectedStep ? "" : "hidden"}>
        ${selectedStepComponents.map((component) => `
          <div class="component-row">
            <span>${escapeHtml(component.name)}</span>
            <strong>${component.value}</strong>
            <button class="danger slim" type="button" data-clear-component="${component.bit}">Clear</button>
          </div>
        `).join("") || "<p>No step components on this step.</p>"}
      </div>
      <form id="componentForm" class="stack-form" ${selectedStep ? "" : "hidden"}>
        <label>Component
          <select id="componentBit">
            ${STEP_COMPONENTS.map((name, bit) => `<option value="${bit}">${escapeHtml(name)}</option>`).join("")}
          </select>
        </label>
        <label>Value<input id="componentValue" type="number" min="0" max="255" value="1" /></label>
        <div class="form-actions">
          <button class="primary" type="submit">Set component</button>
        </div>
      </form>
    </section>
    <section class="inspector-section">
      <h2>Pattern Components</h2>
      <ul>${componentRows || "<li>No step components in this pattern.</li>"}</ul>
    </section>
    <section class="inspector-section">
      <h2>Pattern P-Locks</h2>
      <ul>${plockRows || "<li>No visible p-lock values in this pattern.</li>"}</ul>
    </section>
  `;

  els.side.querySelectorAll("[data-select-note]").forEach((button) => {
    button.addEventListener("click", () => {
      const noteIndex = Number(button.dataset.selectNote);
      state.selectedNote = pattern.notes.find((note) => note.index === noteIndex) ?? null;
      renderSidePanel();
    });
  });
  const addButton = $("#addStepNote");
  if (addButton) addButton.addEventListener("click", addNoteAtSelectedStep);
  els.side.querySelectorAll("[data-step-action]").forEach((button) => {
    button.addEventListener("click", () => runStepAction(button.dataset.stepAction));
  });
  els.side.querySelectorAll("[data-clear-plock]").forEach((button) => {
    button.addEventListener("click", () => clearSelectedStepPlock(Number(button.dataset.clearPlock)));
  });
  els.side.querySelectorAll("[data-clear-component]").forEach((button) => {
    button.addEventListener("click", () => clearSelectedStepComponent(Number(button.dataset.clearComponent)));
  });
  const plockForm = $("#plockForm");
  if (plockForm) plockForm.addEventListener("submit", saveSelectedStepPlock);
  const componentForm = $("#componentForm");
  if (componentForm) componentForm.addEventListener("submit", saveSelectedStepComponent);
  const noteForm = $("#noteForm");
  if (noteForm) noteForm.addEventListener("submit", saveNote);
  const deleteButton = $("#deleteNote");
  if (deleteButton) deleteButton.addEventListener("click", deleteSelectedNote);
}

function saveSelectedStepPlock(event) {
  event.preventDefault();
  if (!state.selectedStep) return;
  const byteOffset = Number($("#plockParam").value);
  const value = clampNumber($("#plockValue").value, 0, 0xffff, 0);
  commitProjectEdit(`Set p-lock on step ${state.selectedStep}.`, () => {
    state.project.setPlockValue(state.activeTrack, state.activePattern, state.selectedStep, byteOffset, value);
  });
}

function clearSelectedStepPlock(byteOffset) {
  if (!state.selectedStep) return;
  commitProjectEdit(`Cleared p-lock on step ${state.selectedStep}.`, () => {
    state.project.clearPlockValue(state.activeTrack, state.activePattern, state.selectedStep, byteOffset);
  });
}

function saveSelectedStepComponent(event) {
  event.preventDefault();
  if (!state.selectedStep) return;
  const bit = Number($("#componentBit").value);
  const value = clampNumber($("#componentValue").value, 0, 0xff, 0);
  commitProjectEdit(`Set step component on step ${state.selectedStep}.`, () => {
    state.project.setStepComponentValue(state.activeTrack, state.activePattern, state.selectedStep, bit, value);
  });
}

function clearSelectedStepComponent(bit) {
  if (!state.selectedStep) return;
  commitProjectEdit(`Cleared step component on step ${state.selectedStep}.`, () => {
    state.project.clearStepComponent(state.activeTrack, state.activePattern, state.selectedStep, bit);
  });
}

function selectedStepNotes() {
  if (!state.project || !state.selectedStep) return [];
  return activePattern().notes.filter((note) => note.step === state.selectedStep);
}

function copySelectedStepNotes() {
  const notes = selectedStepNotes();
  if (!notes.length) {
    setStatus("Selected step has no notes to copy.", "neutral");
    return;
  }

  state.stepClipboard = {
    sourceTrack: state.activeTrack,
    sourcePattern: state.activePattern,
    sourceStep: state.selectedStep,
    notes: notes.map(normalizeClipboardNote),
  };
  setStatus(`Copied ${notes.length} note${notes.length === 1 ? "" : "s"} from step ${state.selectedStep}.`, "ok");
  renderSidePanel();
}

function replaceNotesAtStep(step, sourceNotes, message) {
  if (!state.project || !sourceNotes.length) return;
  commitProjectEdit(message, () => {
    const pattern = activePattern();
    const retained = pattern.notes.filter((note) => note.step !== step);
    const pasted = sourceNotes.map((note) => noteForStep(note, step));
    state.project.setPatternNotes(state.activeTrack, state.activePattern, [...retained, ...pasted]);
    state.selectedStep = step;
    state.selectedNote = null;
  });
}

function pasteStepNotes() {
  if (!state.selectedStep) return;
  if (!state.stepClipboard?.notes?.length) {
    setStatus("Copy notes from a step before pasting.", "neutral");
    return;
  }

  replaceNotesAtStep(
    state.selectedStep,
    state.stepClipboard.notes,
    `Pasted ${state.stepClipboard.notes.length} note${state.stepClipboard.notes.length === 1 ? "" : "s"} to step ${state.selectedStep}.`,
  );
}

function duplicateSelectedStepToNext() {
  if (!state.selectedStep) return;
  const pattern = activePattern();
  if (state.selectedStep >= pattern.steps) {
    setStatus("Selected step is the last step in this pattern.", "neutral");
    return;
  }

  const notes = selectedStepNotes();
  if (!notes.length) {
    setStatus("Selected step has no notes to duplicate.", "neutral");
    return;
  }

  const targetStep = state.selectedStep + 1;
  replaceNotesAtStep(
    targetStep,
    notes.map(normalizeClipboardNote),
    `Duplicated ${notes.length} note${notes.length === 1 ? "" : "s"} to step ${targetStep}.`,
  );
}

function clearSelectedStepNotes() {
  if (!state.selectedStep) return;
  const notes = selectedStepNotes();
  if (!notes.length) {
    setStatus("Selected step has no notes to clear.", "neutral");
    return;
  }

  commitProjectEdit(`Cleared ${notes.length} note${notes.length === 1 ? "" : "s"} from step ${state.selectedStep}.`, () => {
    const pattern = activePattern();
    state.project.setPatternNotes(
      state.activeTrack,
      state.activePattern,
      pattern.notes.filter((note) => note.step !== state.selectedStep),
    );
    state.selectedNote = null;
  });
}

function runStepAction(action) {
  if (action === "copy") copySelectedStepNotes();
  if (action === "paste") pasteStepNotes();
  if (action === "duplicate-next") duplicateSelectedStepToNext();
  if (action === "clear") clearSelectedStepNotes();
}

function addNoteAtSelectedStep() {
  if (!state.selectedStep) return;
  const pattern = activePattern();
  const stepNotes = pattern.notes.filter((note) => note.step === state.selectedStep);
  const lastNote = stepNotes.at(-1);
  state.selectedNote = {
    index: null,
    step: state.selectedStep,
    tickOffset: lastNote?.tickOffset ?? 0,
    gate: lastNote?.gate ?? 240,
    note: lastNote ? Math.min(127, lastNote.note + 4) : 60,
    velocity: lastNote?.velocity ?? 100,
    flags: [0, 0],
  };
  renderSidePanel();
}

function saveNote(event) {
  event.preventDefault();
  const pattern = activePattern();
  const step = clampNumber($("#noteStep").value, 1, pattern.steps, 1);
  const tickOffset = clampNumber($("#noteTickOffset").value, 0, STEP_TICKS - 1, 0);
  const note = {
    step,
    tick: (step - 1) * STEP_TICKS + tickOffset,
    tickOffset,
    note: clampNumber($("#notePitch").value, 0, 127, 60),
    velocity: clampNumber($("#noteVelocity").value, 0, 127, 100),
    gate: clampNumber($("#noteGate").value, 1, 7680, 240),
    flags: [0, 0],
  };

  const index = $("#noteIndex").value === "" ? null : Number($("#noteIndex").value);
  commitProjectEdit("Note changes are in memory. Download the edited .xy when ready.", () => {
    if (index === null) {
      state.project.addNote(state.activeTrack, state.activePattern, note);
    } else {
      state.project.updateNote(state.activeTrack, state.activePattern, index, note);
    }
    state.selectedStep = step;
    state.selectedNote = null;
  });
}

function deleteSelectedNote() {
  const index = $("#noteIndex").value === "" ? null : Number($("#noteIndex").value);
  if (index === null) return;
  commitProjectEdit("Note deleted in memory. Download the edited .xy when ready.", () => {
    state.project.deleteNote(state.activeTrack, state.activePattern, index);
    state.selectedNote = null;
  });
}

function downloadEditedFile() {
  if (!state.project) return;
  const bytes = state.project.encode();
  const blob = new Blob([bytes], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = state.project.name.replace(/\.xy$/i, "") + ".edited.xy";
  a.click();
  URL.revokeObjectURL(url);
}

function bindFileInput() {
  els.fileInput.addEventListener("change", () => {
    const file = els.fileInput.files?.[0];
    if (file) loadFile(file);
  });

  els.dropZone.addEventListener("dragover", (event) => {
    event.preventDefault();
    els.dropZone.classList.add("dragging");
  });
  els.dropZone.addEventListener("dragleave", () => {
    els.dropZone.classList.remove("dragging");
  });
  els.dropZone.addEventListener("drop", (event) => {
    event.preventDefault();
    els.dropZone.classList.remove("dragging");
    const file = event.dataTransfer.files?.[0];
    if (file) loadFile(file);
  });
}

function isEditingElement(target) {
  return ["INPUT", "SELECT", "TEXTAREA"].includes(target?.tagName) || target?.isContentEditable;
}

function navigateSelectedStep(delta) {
  if (!state.project || state.activeView !== "pattern") return;
  const pattern = activePattern();
  const current = state.selectedStep ?? (delta > 0 ? 0 : pattern.steps + 1);
  selectStep(current + delta);
  render();
}

function jumpSelectedStep(step) {
  if (!state.project || state.activeView !== "pattern") return;
  selectStep(step);
  render();
}

function bindKeyboardShortcuts() {
  document.addEventListener("keydown", (event) => {
    if (isEditingElement(event.target)) return;
    const key = event.key.toLowerCase();
    const mod = event.metaKey || event.ctrlKey;

    if (mod && key === "z") {
      event.preventDefault();
      if (event.shiftKey) redoEdit();
      else undoEdit();
      return;
    }

    if (!state.project || state.activeView !== "pattern") return;

    if (event.key === "ArrowRight") {
      event.preventDefault();
      navigateSelectedStep(1);
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      navigateSelectedStep(-1);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      navigateSelectedStep(4);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      navigateSelectedStep(-4);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      jumpSelectedStep(1);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      jumpSelectedStep(activePattern().steps);
      return;
    }
    if (mod && key === "c") {
      event.preventDefault();
      copySelectedStepNotes();
      return;
    }
    if (mod && key === "v") {
      event.preventDefault();
      pasteStepNotes();
      return;
    }
    if (mod && key === "d") {
      event.preventDefault();
      duplicateSelectedStepToNext();
      return;
    }
    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault();
      clearSelectedStepNotes();
    }
  });
}

function boot() {
  Object.assign(els, {
    fileInput: $("#xyFile"),
    dropZone: $("#empty"),
    status: $("#status"),
    tabs: $("#tabs"),
    empty: $("#empty"),
    workspace: $("#workspace"),
    library: $("#library"),
    main: $("#main"),
    side: $("#side"),
    download: $("#download"),
    undo: $("#undo"),
    redo: $("#redo"),
  });

  bindFileInput();
  bindKeyboardShortcuts();
  els.download.addEventListener("click", downloadEditedFile);
  els.undo.addEventListener("click", undoEdit);
  els.redo.addEventListener("click", redoEdit);
  render();
}

boot();
